import React from "react";
import Image from "next/image";
import BaseSectionCard from "@/components/common/BaseSectionCard";
import {
  Sparkles,
  Zap,
  ShieldCheck,
  Users,
  Settings,
  Headphones,
} from "lucide-react";
import solanaTokenCreate from "@/assets/images/solana-token-create.png";
import { TOKEN_TYPE_2022 } from "../constants";
import {
  getSeoContent,
  HOW_TO_STEPS_BY_VARIANT,
  SEO_VARIANT_TOKEN_CREATOR,
} from "../seoContent";

export { default as FAQSection } from "./FAQSection";

export const VideoSection = ({
  variant = SEO_VARIANT_TOKEN_CREATOR,
}) => {
  const seo = getSeoContent(variant);
  return (
  <BaseSectionCard
    className="h-full p-5 sm:p-6 lg:p-8"
    contentClassName="flex flex-col gap-6 sm:gap-8"
  >
    <div className="relative isolate mx-auto w-full max-w-sm sm:max-w-md aspect-video overflow-hidden rounded-2xl border border-gray-700/50 bg-gradient-to-br from-gray-900 to-gray-950 shadow-lg shadow-black/30 ring-1 ring-white/5">
      <Image
        src={solanaTokenCreate}
        alt="Create SPL token on Solana - token creator with name, symbol, decimals and supply"
        fill
        sizes="(max-width: 640px) min(100vw - 32px, 24rem), 28rem"
        className="object-cover object-center"
        priority
      />
    </div>

    <div className="space-y-4 text-gray-300 max-w-prose">
      {seo.videoIntro.map((paragraph) => (
        <p key={paragraph.slice(0, 40)} className="text-sm sm:text-[15px] leading-relaxed">
          {paragraph}
        </p>
      ))}
      <p className="text-sm sm:text-[15px] leading-relaxed">
        Check here a whole blog post about{" "}
        <a
          href="https://www.idxsolana.io/post/create-a-solana-token-in-minutes-no-coding-required"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-cyan-400 underline decoration-cyan-500/40 underline-offset-2 hover:text-cyan-300 hover:decoration-cyan-400 transition-colors"
        >
          how to create a Solana Token
        </a>
        .
      </p>
    </div>
  </BaseSectionCard>
  );
};

export const HowToUseSection = ({
  tokenType,
  variant = SEO_VARIANT_TOKEN_CREATOR,
}) => {
  const isToken2022 = tokenType === TOKEN_TYPE_2022;
  const seo = getSeoContent(variant);

  const steps = isToken2022
    ? [
        {
          title: "Enter Token-2022 Basics",
          subSteps: [
            "Connect your Solana wallet",
            "Enter token name and symbol (max 8 characters)",
            "Set total supply and decimals",
            "Upload token image and add token description",
          ],
        },
        {
          title: "Configure Transfer Fee Settings",
          subSteps: [
            "Set Tax Fee Percent",
            "Set Max Fee (tokens)",
            "Enter Authority Wallet public key",
            "Enter Withdraw Authority public key",
          ],
        },
        {
          title: "Review and Create",
          subSteps: [
            "Add social links and tags",
            "Confirm Token-2022 fee settings and authority wallets",
            "Review all token details carefully",
            "Click Create and approve the transaction",
            "Wait for confirmation and verify on-chain",
          ],
        },
      ]
    : [
        {
          title: "SPL Token Details",
          subSteps: [
            "Connect your Solana wallet",
            "Enter token name and symbol (max 8 characters)",
            "Choose decimals (0 whitelist, 6 utility)",
            "Set token supply",
            "Upload PNG token image",
            "Add token description",
          ],
        },
        {
          title: "Add Social Links & Tags",
          subSteps: [
            "Add project website",
            "Add Twitter / X profile",
            "Add Telegram or Discord",
            "Include tags (DeFi, Meme, Utility)",
          ],
        },
        {
          title: "Revoke Authorities and Create",
          subSteps: [
            "Optionally revoke Mint Authority",
            "Optionally revoke Freeze Authority",
            "Review token details",
            "Click Create and approve transaction",
          ],
        },
      ];

  const variantSteps =
    !isToken2022 && HOW_TO_STEPS_BY_VARIANT[variant]?.length
      ? HOW_TO_STEPS_BY_VARIANT[variant]
      : null;

  const resolvedSteps = variantSteps ?? steps;

  const headingId = `${seo.slugPrefix}-how-to-use-heading`;

  return (
    <section
      className="bg-white/[0.02] backdrop-blur-xl border border-white/5 rounded-3xl p-6 sm:p-8 lg:p-12 relative overflow-hidden h-full flex flex-col"
      aria-labelledby={headingId}
    >
      {/* Glow Effect */}
      <div
        className="absolute top-[-50px] right-[-50px] w-[300px] h-[300px] bg-blue-500/10 rounded-full blur-[80px]"
        aria-hidden="true"
      />

      <div className="relative z-10 flex flex-col flex-1 min-h-0 space-y-10">
        {/* Heading */}
        <div className="text-center space-y-4">
          <h2
            id={headingId}
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight"
          >
            How to use{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              {isToken2022 ? "Token-2022" : seo.howToHeading}
            </span>
            {isToken2022 || seo.howToHeading === "SPL Token" ? " Creator" : null}
          </h2>

          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto">
            {isToken2022
              ? "Follow these steps to create a Token-2022 with transfer fee settings."
              : seo.howToSubtitle}
          </p>
        </div>

        {/* Cards Container */}
        <div className="flex flex-wrap justify-center lg:justify-start gap-6">
          {resolvedSteps.map((step, index) => {
            const stepLabel = String(index + 1).padStart(2, "0");

            return (
              <article
                key={step.title}
                className="w-full sm:w-[350px] flex-shrink-0 bg-gray-900/40 border border-gray-700/50 hover:border-cyan-500/30 rounded-2xl p-6 transition-all duration-300 group"
                aria-label={`Step ${stepLabel}: ${step.title}`}
              >
                {/* Step Number */}
                <div className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-b from-gray-700 to-gray-800 group-hover:from-cyan-900 group-hover:to-blue-900 mb-4 transition-colors">
                  {stepLabel}
                </div>

                {/* Title — non-heading to keep page heading density proportional */}
                <p className="text-lg sm:text-xl font-semibold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                  {step.title}
                </p>

                {/* Sub Steps */}
                <ul className="text-gray-400 text-sm leading-relaxed space-y-2 list-disc list-inside marker:text-gray-500">
                  {step.subSteps.map((sub, subIndex) => (
                    <li key={subIndex}>{sub}</li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const FeaturesSection = () => {
  const features = [
    {
      title: "No-Code Creation",
      description:
        "Create professional SPL tokens without any programming knowledge. Our intuitive interface guides you through every step.",
      icon: Sparkles,
      color: "from-purple-500 to-pink-500",
    },
    {
      title: "Investor-Friendly",
      description:
        "Built-in features like authority revocation, transparent metadata, and multi-wallet distribution to build trust with investors.",
      icon: Users,
      color: "from-blue-500 to-cyan-500",
    },
    {
      title: "Lightning Fast",
      description:
        "Create and deploy tokens in minutes on Solana's high-performance blockchain. No waiting, no delays.",
      icon: Zap,
      color: "from-yellow-500 to-orange-500",
    },
    {
      title: "Enterprise Security",
      description:
        "Military-grade encryption and secure transaction signing. Your keys never leave your wallet.",
      icon: ShieldCheck,
      color: "from-green-500 to-emerald-500",
    },
    {
      title: "Full Customization",
      description:
        "Complete control over token parameters, metadata, social links, and advanced features.",
      icon: Settings,
      color: "from-indigo-500 to-purple-500",
    },
    {
      title: "24/7 Support",
      description:
        "Round-the-clock support from our expert team. Get help whenever you need it.",
      icon: Headphones,
      color: "from-red-500 to-pink-500",
    },
  ];

  return (
    <BaseSectionCard contentClassName="space-y-6 sm:space-y-8 lg:space-y-10">
        <div className="space-y-1">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            Everything You Need to Launch
          </h2>
          <p className="text-sm text-gray-300">
            We&apos;ve built the most comprehensive token creation platform on
            Solana, combining ease of use with powerful features.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="group relative overflow-hidden rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-gray-950/80 p-6 transition-all duration-300 hover:border-gray-700 hover:transform hover:-translate-y-1 hover:shadow-2xl hover:shadow-gray-900/30"
              >
                <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-10 transition-opacity duration-300 from-gray-700 to-transparent" />

                <div className="relative space-y-4">
                  <div className="inline-flex">
                    <div
                      className={`inline-flex items-center justify-center p-3 rounded-xl bg-gradient-to-r ${feature.color}`}
                    >
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xl font-bold text-white">
                      {feature.title}
                    </p>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
    </BaseSectionCard>
  );
};
